import { select, call, put } from "redux-saga/effects";
import { WEEKDAYS } from "lib/date";
import {
  deconstructSchoolID,
  deconstructStudentID,
  deconstructEntityID
} from "lib/appserver/aggregate";
import { sendSchoolMoneyDataRequest } from "state/fetchSagas";
import {
  getSchoolBasketItems,
  getStudentRemoteParentID,
  getClubBookingByID,
  getClubBookingSelectedSession,
  getClubBookingDayDate,
  getDinnerBookingByID,
  getDinnerBookingSelectedOption,
  getDinnerBookingDayDate,
  getPaymentByID,
  isApplePayEnabledForSchool,
  isGooglePayEnabledForSchool
} from "state/school_money/selectors";
import {
  setCheckoutTransactionDetails,
  completeCheckout
} from "state/school_money/actions";
import { getUserIdentity } from "state/app/selectors";
import { secureTradingError } from "state/school_money/actions";
import { getMobileNumber, getDeviceToken, getUserIdentityAppReferrer } from "state/app/selectors";

/**
 * Saga to call the create_transaction API.
 * This sets up a pending transaction on SM's database which we can later
 *   complete using confirm_transaction API.
 * It will also validate our basket items and give us a finalised total.
 */
export default function* createTransactionSaga(action) {

  // transactions are per-school, so our action will specify which one
  const { schoolID } = action.payload;
  const userIdentity = yield select(getUserIdentity);
  const { serverID, remoteSchoolID } = deconstructSchoolID(schoolID);
  const basketItems = yield select(getSchoolBasketItems, schoolID);
  const isApplePayEnabled = yield select(isApplePayEnabledForSchool, schoolID);
  const isGooglePayEnabled = yield select(isGooglePayEnabledForSchool, schoolID);

  // build the list of items
  // this looks like we should be using `basketItems.map()` but we can't
  //   because we need to use `yield` (not possible in nested functions)
  const items = [];
  for (const basketItem of basketItems) {
    items.push(yield* formatBasketItem(basketItem));
  }

  // SEND API REQUEST
  const response = yield call(sendSchoolMoneyDataRequest, "create_transaction", {
    server_id: serverID,
    remote_school_id: remoteSchoolID,
    items: JSON.stringify(items)
  });

  let walletPayJWTString = undefined;

  const { transaction } = response.data;

  if (transaction.total === 0) {
    yield put(completeCheckout());
  } else {

    // Turn off google and apple pay for PA
    if(userIdentity.get("appReferrer") !== process.env.REACT_APP_PARENTAPPS_APP_REFERRER){
        // Get the JWT for apple/google pay if needed
        if (isApplePayEnabled && userIdentity.get("platform") === "iphone" && parseInt(userIdentity.get("osVersion"), 10) >= 13) {
          walletPayJWTString = yield* getApplePayJWT(serverID, remoteSchoolID, transaction);
        }
        else if(isGooglePayEnabled && userIdentity.get("platform") === "android" && parseInt(userIdentity.get("osVersion"), 10) > 5) {
          walletPayJWTString = yield* getGooglePayJWT(serverID, remoteSchoolID, transaction);
        }
    }
    yield put(setCheckoutTransactionDetails(transaction.id, transaction.total, walletPayJWTString));
  }
}

/**
 * Get jwt string to use for apple pay
 */
function* getApplePayJWT(serverID, remoteSchoolID, transaction) {

  const jwtResponse = yield call(sendSchoolMoneyDataRequest, "get_st_apple_pay_jwt", {
    server_id : serverID,
    remote_school_id : remoteSchoolID,
    mobile_number : yield select(getMobileNumber),
    device_token : yield select(getDeviceToken),
    amount : transaction.total,
    transaction_id : transaction.id,
    product: yield select(getUserIdentityAppReferrer)
  });

  // Process failures
  if (jwtResponse.status === "error"){ yield put(secureTradingError({ code : jwtResponse.code, description : jwtResponse.message })); return; }

  return jwtResponse.data.jwt;
}

/**
 * Get jwt string to use for google pay
 */
function* getGooglePayJWT(serverID, remoteSchoolID, transaction) {
  const jwtResponse = yield call(sendSchoolMoneyDataRequest, "get_st_google_pay_jwt", {
    server_id : serverID,
    remote_school_id : remoteSchoolID,
    mobile_number : yield select(getMobileNumber),
    device_token : yield select(getDeviceToken),
    amount : transaction.total,
    transaction_id : transaction.id
  });

  // Process failures
  if (jwtResponse.status === "error"){ yield put(secureTradingError({ code : jwtResponse.code, description : jwtResponse.message })); return; }

  return jwtResponse.data.jwt;
}

/**
 * Delegate to payload formatting functions for each item type.
 */
function* formatBasketItem(basketItem) {
  switch (basketItem.type) {
    case "clubBooking": return yield* formatClubBasketItem(basketItem);
    case "dinnerBooking": return yield* formatDinnerBasketItem(basketItem);
    case "payment": return yield* formatPaymentBasketItem(basketItem);
    case "shopItem": return yield* formatShopItemBasketItem(basketItem);
    case "creditTopUp": return yield* formatCreditTopUpBasketItem(basketItem);
    default: return undefined;
  }
}

function* formatClubBasketItem({ studentID, price, payload }) {

  const clubBookingID = payload.get("clubBookingID");
  const clubBooking = yield select(getClubBookingByID, clubBookingID);
  const { remoteStudentID, remoteInstallmentID, remoteID } = clubBooking;
  const remoteParentID = yield select(getStudentRemoteParentID, studentID);

  const bookings = [];
  for (let weekdayIndex = 0; weekdayIndex < WEEKDAYS.length; weekdayIndex++) {
    const selectedSession = yield select(getClubBookingSelectedSession, clubBookingID, weekdayIndex);
    const date = yield select(getClubBookingDayDate, clubBookingID, weekdayIndex);
    // only add session details if a session is booked
    if (selectedSession === undefined) {
      bookings.push({ club_date: date, status: "N" });
    } else {
      const { id, price, description } = selectedSession;
      bookings.push({ club_date: date, status: "Y", session_booked: { id, price, description } });
    }
  }

  return {
    id: remoteID,
    type: "club",
    payload: {
      student_fk: remoteStudentID,
      parent_fk: remoteParentID,
      installment_fk: remoteInstallmentID,
      amount: price,
      monday_booking: bookings[0],
      tuesday_booking: bookings[1],
      wednesday_booking: bookings[2],
      thursday_booking: bookings[3],
      friday_booking: bookings[4]
    }
  }

}

function* formatDinnerBasketItem({ studentID, price, payload }) {

  const dinnerBookingID = payload.get("dinnerBookingID");
  const dinnerBooking = yield select(getDinnerBookingByID, dinnerBookingID);
  const { remoteStudentID, remoteInstallmentID, remoteID, type, isCashless } = dinnerBooking;
  const remoteParentID = yield select(getStudentRemoteParentID, studentID);

  const bookings = [];
  for (let weekdayIndex = 0; weekdayIndex < WEEKDAYS.length; weekdayIndex++) {

    const selectedOption = yield select(getDinnerBookingSelectedOption, dinnerBookingID, weekdayIndex);
    const date = yield select(getDinnerBookingDayDate, dinnerBookingID, weekdayIndex);
    const isSelected = selectedOption !== null;

    if (isCashless) {

      // cashless dinners require the item object if status == "MEAL"
      const booking = { dinner_date: date };
      booking.status = isSelected ? "MEAL" : "NONE";
      if (isSelected) {
        const { id, name, price } = selectedOption;
        booking.item = { id, name, price };
      }
      bookings.push(booking);

    } else {

      // noncashless/meat-veg requires only the status
      bookings.push({
        dinner_date: date,
        status: isSelected ? selectedOption.id : "NONE"
      });

    }

  }

  return {
    id: remoteID,
    type: "dinner",
    payload: {
      student_fk: remoteStudentID,
      parent_fk: remoteParentID,
      installment_fk: remoteInstallmentID,
      amount: price,
      dinner_type: type,
      monday_booking: bookings[0],
      tuesday_booking: bookings[1],
      wednesday_booking: bookings[2],
      thursday_booking: bookings[3],
      friday_booking: bookings[4]
    }
  }

}

function* formatPaymentBasketItem({ studentID, price, payload }) {

  const { paymentID, installmentNumber } = payload.toObject();
  const payment = yield select(getPaymentByID, paymentID);
  const { remoteStudentID, remoteInstallmentID, remoteID } = payment;
  const remoteParentID = yield select(getStudentRemoteParentID, studentID);

  return {
    id: remoteID,
    type: "payment",
    payload: {
      student_fk: remoteStudentID,
      parent_fk: remoteParentID,
      installment_fk: remoteInstallmentID,
      installment_number: installmentNumber,
      amount: price
    }
  }

}

function* formatShopItemBasketItem({ studentID, price, payload }) {

  const { remoteStudentID } = deconstructStudentID(studentID);
  const { shopItemID, quantity } = payload.toObject();
  const { remoteID } = deconstructEntityID(shopItemID);
  const remoteParentID = yield select(getStudentRemoteParentID, studentID);

  return {
    id: remoteID,
    type: "shop_item",
    payload: {
      student_fk: remoteStudentID,
      parent_fk: remoteParentID,
      amount: price,
      quantity
    }
  }

}

function* formatCreditTopUpBasketItem({ studentID, price, payload }) {

  const { remoteStudentID } = deconstructStudentID(studentID);
  const { creditType } = payload.toObject();
  const remoteParentID = yield select(getStudentRemoteParentID, studentID);

  return {
    id: creditType,
    type: "top_up",
    payload: {
      student_fk: remoteStudentID,
      parent_fk: remoteParentID,
      amount: price
    }
  }

}



// WEBPACK FOOTER //
// ./src/state/school_money/sagas/createTransactionSaga.js